import { Controller } from "@hotwired/stimulus"

// Agency Program 2f (#7048): live, advisory preview of the owner-funded match on
// the Buy-Now lead-order form. The legacy inline jQuery writes the order total
// into #lead_order_total via .val() (which fires no DOM event), so we POLL that
// field rather than listen — this keeps the preview fully decoupled from the
// debounced total-price AJAX. Matched checkout is one-time/Buy-Now only, so the
// control hides itself when the frequency selector is on a subscription. The
// authoritative clamp (exact share, per-use/cumulative/interval caps, race-safe
// funds check) runs server-side in Agency::MatchedCheckout::Reserve; the figures
// here are an estimate only — `coverable` already nets the owner's caps + balance.
const STRIPE_MIN_CHARGE_CENTS = 50
const TOTAL_FIELD_ID = "lead_order_total"
const FREQUENCY_FIELD_ID = "lead_order_frequency"
const POLL_MS = 500

export default class extends Controller {
  static targets = ["preview", "checkbox"]
  static values = {
    percent: Number,
    perUseCapCents: Number, // 0 = no per-use cap
    coverableCents: Number, // owner available already clamped by cumulative/interval caps
  }

  connect() {
    this.boundUpdate = () => this.update()
    this.frequencyField = document.getElementById(FREQUENCY_FIELD_ID)
    this.frequencyField?.addEventListener("change", this.boundUpdate)
    this.update()
    this.timer = setInterval(this.boundUpdate, POLL_MS)
  }

  disconnect() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.frequencyField?.removeEventListener("change", this.boundUpdate)
  }

  update() {
    if (!this.hasPreviewTarget) return

    // Matched checkout applies to one-time/Buy-Now orders only — hide + uncheck on
    // a subscription frequency so the preview never promises an uncreditable match.
    if (!this.appliesToFrequency()) {
      this.element.hidden = true
      if (this.hasCheckboxTarget) this.checkboxTarget.checked = false
      return
    }
    this.element.hidden = false

    const totalCents = this.readTotalCents()
    if (totalCents === null) return // leave the server-rendered "enter a quantity" hint

    const covered = this.coveredCents(totalCents)
    if (covered <= 0) {
      this.previewTarget.textContent = "This order total is too low to apply your match yet."
      return
    }
    const card = totalCents - covered
    this.previewTarget.textContent =
      `Your owner covers up to ${this.formatCurrency(covered)} on this order — you'd pay ${this.formatCurrency(card)}.`
  }

  // Owner share, mirroring Reserve#compute_owner_cents: ceil(total × pct), then
  // clamped to the per-use cap, the owner's coverable balance (already net of the
  // cumulative/interval caps), and a remainder that keeps the agent's card charge
  // at/above the Stripe minimum.
  coveredCents(totalCents) {
    let covered = Math.ceil((totalCents * this.percentValue) / 100)
    if (this.perUseCapCentsValue > 0) covered = Math.min(covered, this.perUseCapCentsValue)
    covered = Math.min(covered, this.coverableCentsValue, totalCents - STRIPE_MIN_CHARGE_CENTS)
    return Math.max(covered, 0)
  }

  // Matched checkout is one-time only; treat a missing selector (subscriptions off)
  // as one-time, since the order then defaults to one_time.
  appliesToFrequency() {
    const field = document.getElementById(FREQUENCY_FIELD_ID)
    return !field || field.value === "one_time"
  }

  // The legacy total field holds "($1234.50)" once computed, or a prompt string
  // otherwise. Returns cents, or null when no dollar amount is present yet.
  readTotalCents() {
    const field = document.getElementById(TOTAL_FIELD_ID)
    if (!field) return null
    const match = String(field.value).match(/([\d,]+\.\d{2})/)
    if (!match) return null
    return Math.round(parseFloat(match[1].replace(/,/g, "")) * 100)
  }

  formatCurrency(cents) {
    return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })
  }
}
